import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  RefreshCw,
  Eye,
  EyeOff,
  Star,
  AlertTriangle,
  Zap,
  Database
} from 'lucide-react'
import api from '../utils/api'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import { JOB_STATUS_LABELS, JOB_STATUS_COLORS } from '../utils/constants'
import TranslationFileProcessor from '../utils/TranslationFileProcessor'
import TranslationTable from '../components/review/TranslationTable'
import ReviewSettingsManager from '../utils/ReviewSettingsManager'

const ReviewPage = () => {
  console.log('🔥 REVIEW PAGE COMPONENT MOUNTING - jobId from URL:', window.location.pathname)
  
  const { t } = useTranslation()
  const { jobId } = useParams()
  const navigate = useNavigate()

  // Helper function to calculate job duration
  const calculateDuration = (job) => {
    if (job.duration_display) {
      return job.duration_display
    }
    
    // For completed/finished jobs, calculate duration from timestamps
    if ((job.status === 'completed' || job.status === 'finished') && job.created_at) {
      const startTime = new Date(job.created_at)
      const endTime = job.updated_at ? new Date(job.updated_at) : new Date()
      const durationMs = endTime - startTime
      
      if (durationMs > 0) {
        const totalSeconds = Math.round(durationMs / 1000) // Round instead of floor for better accuracy
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        
        if (hours > 0) {
          return `${hours}h ${minutes}m`
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`
        } else {
          return `${seconds}s`
        }
      }
    }
    
    return 'N/A'
  }
  
  console.log('🔥 REVIEW PAGE - Extracted jobId:', jobId)
  
  const [showTranslations, setShowTranslations] = useState(true)
  const [reviewTriggered, setReviewTriggered] = useState(false)
  
  // File processor with settings persistence
  const settingsManagerRef = useRef(ReviewSettingsManager)
  const fileProcessorRef = useRef(null)
  const [processorState, setProcessorState] = useState({
    isLoaded: false,
    isLoading: false,
    error: null,
    data: [],
    headers: [],
    pagination: null,
    languageInfo: null,
    searchValue: ''
  })

  // Initialize file processor with settings when jobId is available
  useEffect(() => {
    if (jobId && !fileProcessorRef.current) {
      fileProcessorRef.current = new TranslationFileProcessor(settingsManagerRef.current, jobId)
      console.log('📋 Initialized TranslationFileProcessor with settings for job:', jobId)
    }
  }, [jobId])

  // Add early safety check
  if (!jobId) {
    console.log('❌ No jobId found, redirecting...')
    return (
      <div style={{ padding: '20px', background: 'red', color: 'white' }}>
        <h1>ERROR: No Job ID Found</h1>
        <p>jobId: {JSON.stringify(jobId)}</p>
        <p>URL: {window.location.pathname}</p>
        <button onClick={() => navigate('/translate')}>Back to Translations</button>
      </div>
    )
  }

  // Fetch job details with enhanced metadata
  const { data: job, isLoading: jobLoading, error: jobError } = useQuery({
    queryKey: ['job-enhanced', jobId],
    queryFn: async () => {
      console.log('📋 Fetching job details for:', jobId)
      const response = await api.get(`/user/jobs/${jobId}/enhanced`)
      console.log('📋 Job details response:', response.data)
      return response.data
    },
    onError: (error) => {
      console.error('❌ Job details fetch failed:', error)
      console.error('Error details:', error.response?.data)
    },
  })

  // Fetch quota status for validation
  const { data: quotaStatus } = useQuery({
    queryKey: ['quota-status'],
    queryFn: async () => {
      try {
        const response = await api.get('/user/quota/status')
        return response.data
      } catch (error) {
        console.warn('Failed to load quota status in ReviewPage:', error)
        return {
          current_usage: 0,
          monthly_limit: 10000,
          percentage_used: 0,
          is_exceeded: false,
          can_start_jobs: true,
          warning_level: 'normal',
          error: 'Failed to load quota status'
        }
      }
    },
    refetchInterval: 60000, // Check every minute
    retry: 3,
  })

  // Fetch translated file content for display (use content_url, not download_url)
  const { data: fileContent, isLoading: fileLoading, error: fileError } = useQuery({
    queryKey: ['job-file-content', jobId],
    queryFn: async () => {
      if (!job?.content_url && job?.status !== 'completed' && job?.status !== 'finished') {
        throw new Error('Job not completed or no content URL available')
      }
      
      // Use content_url for review page display (returns JSON data)
      const contentUrl = job.content_url || `/api/v1/user/jobs/${jobId}/content`
      const response = await fetch(contentUrl)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.statusText}`)
      }
      
      // Parse as JSON instead of text since content endpoint returns JSON
      const jsonData = await response.json()
      return jsonData
    },
    enabled: !!job && (job.status === 'completed' || job.status === 'finished'),
  })

  // Process translation data with enhanced file processor
  useEffect(() => {
    if (fileContent && !processorState.isLoading && fileProcessorRef.current) {
      const loadTranslationData = async () => {
        try {
          setProcessorState(prev => ({ ...prev, isLoading: true, error: null }))
          
          const processor = fileProcessorRef.current
          processor.reset()
          
          // Load data using the file processor with settings persistence
          await processor.loadData({
            type: 'json',
            data: fileContent
          }, {
            sourceLanguage: job?.source_lang,
            targetLanguages: Array.isArray(job?.target_langs) ? job.target_langs : 
                            job?.target_langs ? job.target_langs.split(',').map(l => l.trim()) : []
          })
          
          // Get initial paginated data (includes settings restoration)
          const paginatedResult = processor.getPaginatedData()
          
          setProcessorState({
            isLoaded: true,
            isLoading: false,
            error: null,
            data: paginatedResult.data,
            headers: paginatedResult.headers,
            pagination: paginatedResult.pagination,
            languageInfo: paginatedResult.languages,
            searchValue: processor.searchFilter // Get restored search value
          })
          
          console.log('✅ Enhanced file processor loaded with persistent settings:', {
            performance: processor.getPerformanceInfo(),
            settings: settingsManagerRef.current.getDebugInfo()
          })
          
        } catch (error) {
          console.error('❌ Failed to process translation data:', error)
          setProcessorState(prev => ({
            ...prev,
            isLoading: false,
            error: error,
            isLoaded: false
          }))
        }
      }
      
      loadTranslationData()
    }
  }, [fileContent, job?.source_lang, job?.target_langs])

  // Trigger AI review
  const reviewMutation = useMutation({
    mutationFn: async () => {
      // Pre-review quota validation
      if (quotaStatus?.is_exceeded) {
        throw new Error('Cannot start AI review: Monthly quota exceeded. Please wait for renewal or upgrade your plan.')
      }
      
      console.log('🚀 Triggering review for job:', jobId)
      const response = await api.post(`/user/jobs/${jobId}/review`, {})
      console.log('✅ Review trigger response:', response.data)
      return response.data
    },
    onSuccess: (data) => {
      console.log('✅ Review triggered successfully:', data)
      setReviewTriggered(true)
      // Immediately refetch review status after triggering
      console.log('📡 Triggering immediate refetch after successful review start')
      refetchReview()
    },
    onError: (error) => {
      console.error('❌ Review trigger failed:', error)
      console.error('Error details:', error.response?.data)
      // Don't set reviewTriggered to true on error
    },
  })

  // Fetch AI review results - always check for existing reviews
  const { data: reviewResults, isLoading: reviewLoading, refetch: refetchReview } = useQuery({
    queryKey: ['job-review-results', jobId],
    queryFn: async () => {
      console.log('🔍 Fetching review results for job:', jobId)
      const response = await api.get(`/user/jobs/${jobId}/review/result`)
      console.log('📄 Review results response:', response.data)
      return response.data
    },
    enabled: true, // Always enabled to check for existing reviews
    refetchInterval: reviewTriggered ? 3000 : false, // Poll every 3 seconds only when review is active
    onError: (error) => {
      console.error('❌ Review results fetch failed:', error)
      console.error('Error details:', error.response?.data)
    },
  })

  // Set reviewTriggered based on review status
  React.useEffect(() => {
    console.log('🔄 Review status changed:', reviewResults?.review_status)
    if (reviewResults?.review_status && reviewResults.review_status !== 'not_started') {
      console.log('✅ Setting reviewTriggered to true')
      setReviewTriggered(true)
    } else {
      console.log('ℹ️ Review not started, keeping reviewTriggered false')
    }
  }, [reviewResults?.review_status])

  // Simplified polling effect
  React.useEffect(() => {
    if (!reviewTriggered) return

    const isProcessing = reviewResults?.review_status === 'review_processing' || 
                        reviewResults?.review_status === 'review_pending'
    
    console.log('🔄 Polling check:', { status: reviewResults?.review_status, isProcessing })
    
    if (isProcessing) {
      console.log('🔄 Starting polling for status:', reviewResults?.review_status)
      
      const pollInterval = setInterval(() => {
        console.log('📡 Polling - refetching review results')
        refetchReview()
      }, 3000) // Poll every 3 seconds

      return () => {
        console.log('🛑 Stopping polling')
        clearInterval(pollInterval)
      }
    }
  }, [reviewTriggered, reviewResults?.review_status, refetchReview])

  // Translation table event handlers
  const handlePageChange = (page) => {
    const processor = fileProcessorRef.current
    const result = processor.goToPage(page)
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      pagination: result.pagination
    }))
  }

  const handleItemsPerPageChange = (itemsPerPage) => {
    const processor = fileProcessorRef.current
    const result = processor.setItemsPerPage(itemsPerPage)
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      pagination: result.pagination
    }))
  }

  const handleLanguageToggle = (language) => {
    const processor = fileProcessorRef.current
    const currentVisible = processor.visibleLanguages
    const newVisible = currentVisible.includes(language)
      ? currentVisible.filter(lang => lang !== language)
      : [...currentVisible, language]
    
    const result = processor.setVisibleLanguages(newVisible)
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      headers: result.headers,
      languageInfo: result.languages
    }))
  }

  const handleShowAllLanguages = () => {
    const processor = fileProcessorRef.current
    const result = processor.showAllLanguages()
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      headers: result.headers,
      languageInfo: result.languages
    }))
  }

  const handleHideAllLanguages = () => {
    const processor = fileProcessorRef.current
    // Keep at least the source language visible
    const sourceLanguage = processor.sourceLanguage
    const result = processor.setVisibleLanguages(sourceLanguage ? [sourceLanguage] : [processor.allLanguages[0]])
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      headers: result.headers,
      languageInfo: result.languages
    }))
  }

  const handleResetLanguages = () => {
    const processor = fileProcessorRef.current
    processor._initializeVisibleLanguages()
    const result = processor.getPaginatedData()
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      headers: result.headers,
      languageInfo: result.languages
    }))
  }

  const handleSearchChange = (searchValue) => {
    const processor = fileProcessorRef.current
    const result = processor.setSearchFilter(searchValue)
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      pagination: result.pagination,
      searchValue
    }))
  }

  const handleClearSearch = () => {
    const processor = fileProcessorRef.current
    const result = processor.clearSearch()
    setProcessorState(prev => ({
      ...prev,
      data: result.data,
      pagination: result.pagination,
      searchValue: ''
    }))
  }

  const handleExportFiltered = (format = 'csv') => {
    try {
      const processor = fileProcessorRef.current
      const exportData = processor.exportFilteredData(format)
      
      // Create and download file
      const blob = new Blob([exportData], { type: format === 'csv' ? 'text/csv' : 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `translation-review-filtered.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      console.log('✅ Exported filtered data:', format)
    } catch (error) {
      console.error('❌ Export failed:', error)
    }
  }

  // Debug logging
  console.log('🎯 ReviewPage render state:', {
    jobId,
    jobLoading,
    reviewLoading,
    reviewTriggered,
    jobStatus: job?.status,
    reviewStatus: reviewResults?.review_status,
    hasReviewResult: reviewResults?.has_review_result,
    mutationPending: reviewMutation.isPending,
    processorLoaded: processorState.isLoaded,
    dataRows: processorState.data.length
  })

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finished':
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />
      case 'processing':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />
    }
  }

  if (jobLoading) {
    console.log('⏳ Job loading...')
    return (
      <div className="page-transition">
        <div className="flex items-center justify-center min-h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2">{t('review.loading.jobDetails')}</span>
        </div>
      </div>
    )
  }

  if (jobError || !job) {
    console.log('❌ Job error or no job found:', { jobError, job })
    return (
      <div className="page-transition">
        <Card>
          <Card.Content className="text-center py-8">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('review.errors.jobNotFound')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('review.errors.jobNotFoundDescription')}
            </p>
            <Button onClick={() => navigate('/translate')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('review.backToTranslations')}
            </Button>
          </Card.Content>
        </Card>
      </div>
    )
  }

  return (
    <div className="page-transition space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/translate')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('review.backToTranslations')}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('review.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t('review.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {job.download_url && (
            <Button
              variant="outline"
              onClick={() => window.open(job.download_url, '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              {t('review.downloadResult')}
            </Button>
          )}
        </div>
      </div>

      {/* Job Information */}
      <Card>
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            {getStatusIcon(job.status)}
            <span>{t('review.jobInfo.title')}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${JOB_STATUS_COLORS[job.status]}`}>
              {t(JOB_STATUS_LABELS[job.status])}
            </span>
          </Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.jobId')}</p>
              <p className="text-sm text-gray-900 dark:text-white font-mono">{job.job_id || job.id}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.sourceLanguage')}</p>
              <p className="text-sm text-gray-900 dark:text-white">{job.source_lang}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.targetLanguages')}</p>
              <p className="text-sm text-gray-900 dark:text-white">
                {Array.isArray(job.target_langs) ? job.target_langs.join(', ') : job.target_langs}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.created')}</p>
              <p className="text-sm text-gray-900 dark:text-white">
                {job.created_display || new Date(job.created_at).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Enhanced Metadata Section */}
          {job.has_llm_data && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t('review.jobInfo.processingMetrics')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.duration')}</p>
                  <p className="text-sm text-gray-900 dark:text-white">{calculateDuration(job)}</p>
                </div>
                <div>
                  {(() => {
                    const characters = job.total_characters_used || job.llm_total_characters || 0;
                    const tokens = job.total_tokens_used || job.llm_total_tokens || 0;
                    
                    if (characters > 0) {
                      return (
                        <>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.totalCharacters')}</p>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {characters.toLocaleString()}
                          </p>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.totalTokens')}</p>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {tokens.toLocaleString()}
                          </p>
                        </>
                      );
                    }
                  })()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.apiRequests')}</p>
                  <p className="text-sm text-gray-900 dark:text-white">{job.llm_request_count || 0}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('review.jobInfo.languageCount')}</p>
                  <p className="text-sm text-gray-900 dark:text-white">{job.target_lang_count || 1}</p>
                </div>
              </div>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Enhanced Translation Content with Client-Side Processing */}
      <Card>
        <Card.Header className="flex items-center justify-between">
          <Card.Title className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-blue-500" />
            <span>{t('review.content.title')}</span>
            {processorState.isLoaded && (
              <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                <Database className="w-3 h-3" />
                <span>Client-side processing</span>
              </div>
            )}
          </Card.Title>
          <div className="flex items-center space-x-2">
            {processorState.isLoaded && (
              <div className="flex items-center space-x-3 text-xs text-gray-600 dark:text-gray-400">
                <span>
                  {processorState.pagination?.totalRows.toLocaleString()} rows • {processorState.languageInfo?.allLanguages.length} languages
                </span>
                <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                  <Database className="w-3 h-3" />
                  <span>Settings saved</span>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTranslations(!showTranslations)}
            >
              {showTranslations ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  {t('review.content.hideContent')}
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  {t('review.content.showContent')}
                </>
              )}
            </Button>
          </div>
        </Card.Header>
        {showTranslations && (
          <Card.Content>
            <TranslationTable
              data={processorState.data}
              headers={processorState.headers}
              pagination={processorState.pagination}
              languageInfo={processorState.languageInfo}
              searchValue={processorState.searchValue}
              isLoading={fileLoading || processorState.isLoading}
              error={fileError || processorState.error}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onLanguageToggle={handleLanguageToggle}
              onShowAllLanguages={handleShowAllLanguages}
              onHideAllLanguages={handleHideAllLanguages}
              onResetLanguages={handleResetLanguages}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
              onExportFiltered={handleExportFiltered}
            />
          </Card.Content>
        )}
      </Card>


      {/* AI Review Section */}
      <Card>
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <Star className="w-5 h-5" />
            <span>{t('review.aiReview.title')}</span>
          </Card.Title>
        </Card.Header>
        <Card.Content>
          
          {/* Not started or initial state */}
          {!reviewTriggered && (!reviewResults || reviewResults.review_status === 'not_started') ? (
            <div className="text-center py-8">
              <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('review.aiReview.available')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('review.aiReview.availableDescription')}
              </p>
              <div className="relative group">
                <Button
                  onClick={() => reviewMutation.mutate()}
                  loading={reviewMutation.isPending}
                  disabled={
                    (job.status !== 'completed' && job.status !== 'finished') || 
                    quotaStatus?.is_exceeded
                  }
                  title={quotaStatus?.is_exceeded ? 
                    `Quota exceeded: ${(quotaStatus.current_usage || 0).toLocaleString()} / ${(quotaStatus.monthly_limit || 0).toLocaleString()} characters used` : 
                    undefined
                  }
                >
                  <Play className="w-4 h-4 mr-2" />
                  {quotaStatus?.is_exceeded ? 'Review Blocked' : t('review.aiReview.startReview')}
                </Button>
                {quotaStatus?.is_exceeded && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span>Quota exceeded: Review blocked until renewal</span>
                    </div>
                  </div>
                )}
              </div>
              {(job.status !== 'completed' && job.status !== 'finished') && !quotaStatus?.is_exceeded && (
                <p className="text-sm text-gray-500 mt-2">
                  {t('review.aiReview.onlyForCompleted')}
                </p>
              )}
              {quotaStatus?.is_exceeded && (
                <p className="text-sm text-red-500 mt-2">
                  {t('quota.exceeded.cannotStart')} - {quotaStatus.days_until_renewal ? 
                    t('quota.exceeded.renewsIn', { days: quotaStatus.days_until_renewal }) : 
                    'Please upgrade your plan'
                  }
                </p>
              )}
            </div>
          ) : (reviewResults?.review_status === 'review_pending' || 
                 reviewResults?.review_status === 'review_processing' || 
                 (reviewLoading && reviewTriggered)) ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('review.aiReview.inProgress')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('review.aiReview.inProgressDescription')}
              </p>
              <div className="mt-4">
                <div className="text-sm text-gray-500">
                  Status: {reviewResults?.review_status === 'review_pending' ? t('review.aiReview.statusLabels.queued') : 
                           reviewResults?.review_status === 'review_processing' ? t('review.aiReview.statusLabels.processing') : t('review.aiReview.statusLabels.loading')}
                </div>
              </div>
            </div>
          ) : reviewResults?.review_status === 'review_failed' ? (
            <div className="text-center py-8">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('review.aiReview.failed')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {reviewResults.error || t('review.aiReview.failedDescription')}
              </p>
              <div className="relative group">
                <Button
                  onClick={() => reviewMutation.mutate()}
                  loading={reviewMutation.isPending}
                  variant="outline"
                  disabled={quotaStatus?.is_exceeded}
                  title={quotaStatus?.is_exceeded ? 
                    `Quota exceeded: ${(quotaStatus.current_usage || 0).toLocaleString()} / ${(quotaStatus.monthly_limit || 0).toLocaleString()} characters used` : 
                    undefined
                  }
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {quotaStatus?.is_exceeded ? 'Retry Blocked' : t('review.aiReview.retryReview')}
                </Button>
                {quotaStatus?.is_exceeded && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span>Quota exceeded: Review blocked until renewal</span>
                    </div>
                  </div>
                )}
              </div>
              {quotaStatus?.is_exceeded && (
                <p className="text-sm text-red-500 mt-2">
                  {t('quota.exceeded.cannotStart')} - {quotaStatus.days_until_renewal ? 
                    t('quota.exceeded.renewsIn', { days: quotaStatus.days_until_renewal }) : 
                    'Please upgrade your plan'
                  }
                </p>
              )}
            </div>
          ) : reviewResults?.review_status === 'review_completed' ? (
            // Handle both successful completion and completion without results
            reviewResults.has_review_result ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('review.aiReview.completed')}
                </h3>
                <div className="flex space-x-2">
                  {reviewResults.download_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(reviewResults.download_url, '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('review.aiReview.downloadResults')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => refetchReview()}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('review.aiReview.refresh')}
                  </Button>
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <h4 className="font-medium text-green-900 dark:text-green-100">
                      {t('review.aiReview.completed')}
                    </h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {t('review.aiReview.completedDescription')}
                    </p>
                  </div>
                </div>
              </div>
              
              {reviewResults.download_url && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {t('review.aiReview.fileInfo')}
                  </h4>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>File: {reviewResults.review_file?.split('/').pop() || 'output_review.csv'}</p>
                    <p>{t('review.aiReview.fileFormat')}</p>
                  </div>
                </div>
              )}
            </div>
            ) : (
              // Review completed but no results available
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {t('review.aiReview.noResults')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('review.aiReview.noResultsDescription')}
                </p>
                <div className="flex justify-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => refetchReview()}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('review.aiReview.refreshStatus')}
                  </Button>
                  <div className="relative group">
                    <Button
                      onClick={() => reviewMutation.mutate()}
                      loading={reviewMutation.isPending}
                      variant="outline"
                      size="sm"
                      disabled={quotaStatus?.is_exceeded}
                      title={quotaStatus?.is_exceeded ? 
                        `Quota exceeded: ${(quotaStatus.current_usage || 0).toLocaleString()} / ${(quotaStatus.monthly_limit || 0).toLocaleString()} characters used` : 
                        undefined
                      }
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {quotaStatus?.is_exceeded ? 'Retry Blocked' : t('review.aiReview.retryReview')}
                    </Button>
                    {quotaStatus?.is_exceeded && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                          <span>Quota exceeded: Review blocked</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : (
            // Fallback for any unexpected states
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('review.aiReview.unknownState')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {reviewResults?.review_status ? 
                  t('review.aiReview.unexpectedStatus', { status: reviewResults.review_status }) : 
                  t('review.aiReview.unknownStateDescription')
                }
              </p>
              <div className="flex justify-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetchReview()}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('review.aiReview.refresh')}
                </Button>
                <div className="relative group">
                  <Button
                    onClick={() => reviewMutation.mutate()}
                    loading={reviewMutation.isPending}
                    variant="outline"
                    size="sm"
                    disabled={quotaStatus?.is_exceeded}
                    title={quotaStatus?.is_exceeded ? 
                      `Quota exceeded: ${(quotaStatus.current_usage || 0).toLocaleString()} / ${(quotaStatus.monthly_limit || 0).toLocaleString()} characters used` : 
                      undefined
                    }
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {quotaStatus?.is_exceeded ? 'Review Blocked' : t('review.aiReview.startNewReview')}
                  </Button>
                  {quotaStatus?.is_exceeded && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span>Quota exceeded: Review blocked</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  )
}

export default ReviewPage